import { compose } from "@insightfulscience/shared-utils/fn";
import SN from "../../config/serialNumbers.json";
import getCartQuery, { readCart } from "../graphql/queries/getCart";
import nextSingleton from "../utils/nextSignleton";
import apolloClient from "../apollo";
import getPlanWithPricesQuery, {
	packPlanCode,
	readPlan,
	readPrices,
} from "../graphql/queries/getPlanWithPrices";
import purchaseMutation, { readPaymentResult } from "../graphql/mutations/purchase";
import updateSubscriptionMutation, {
	readUpdateSubscriptionResult,
} from "../graphql/mutations/subscriptionPurchaseActivations";
import updatePaymentMethodMutation from "../graphql/mutations/updatePaymentMethodMutation";
import getSubscriptionByIDQuery, {
	readSubscriptionData,
} from "../graphql/queries/getSubscriptionByID";
import getPerpetualsQuery, { readPerpetuals } from "../graphql/queries/getPerpetuals";
import getPerpetualByIdQuery, { readDataFromPerpetual } from "../graphql/queries/getPerpetual";
import getSubscriptionsListQuery, {
	readSubscriptions,
} from "../graphql/queries/getSubscriptionsList";
import getRenewableSubscriptionsQuery, {
	readRenewableSubscriptions,
} from "../graphql/queries/getRenewableSubscriptions";
import getOrderByIDQuery, { readOrderData } from "../graphql/queries/getOrderByID";
import removeActivationFromBlackListMutation from "../graphql/mutations/removeActivationFromBlackList";
import addActivationToBlackListMutation from "../graphql/mutations/addActivationToBlackList";
import cancelSubscriptionMutation from "../graphql/mutations/cancelSubscriptionMutation";
import cancelSubscriptionWithSurveyMutation, {
	readCancelSubscriptionByIDV2Result,
} from "../graphql/mutations/cancelSubscriptionWithSurveyMutation";
import reactivateSubscriptionMutation, {
	readReactivateSubscriptionByIDV2Result,
} from "../graphql/mutations/reactivateSubscriptionMutation";
import getSubscriptionWithRenewPrices from "../graphql/queries/getSubscriptionWithRenewPrices";
import getSubscriptionWithAddonPrices from "../graphql/queries/getSubscriptionWithAddonPrices";
import SubscriptionWithPaymentMethod from "../graphql/queries/SubscriptionWithPaymentMethod";
import getThreePlanPricesQuery, {
	readThreePlanPrices,
} from "../graphql/queries/getThreePlanPrices";

export { getSubscriptionFlags } from "./subscriptionFlags";

const getPrices = context => planCode =>
	apolloClient(context)
		.query({
			query: getPlanWithPricesQuery,
			variables: packPlanCode(planCode),
		})
		.then(compose(readPrices, readPlan));

const getCart = context => (selectedItem, billingInfo) =>
	apolloClient(context)
		.query({
			query: getCartQuery,
			variables: {
				cartItems: [
					{
						planCode: selectedItem.planCode,
						subscriptionSize: selectedItem.seats,
						licenseName: billingInfo.labName,
						newSeats: selectedItem.seats,
						subscriptionID: selectedItem.subscriptionID,
						purchaseType: selectedItem.purchaseType,
					},
				],
				billingInfo: {
					email: billingInfo.email,
					firstName: billingInfo.firstName,
					lastName: billingInfo.lastName,
					company: billingInfo.company,
					labName: billingInfo.labName,
					countryID: billingInfo.countryID,
					state: billingInfo.state,
					city: billingInfo.city,
					address1: billingInfo.address1,
					address2: billingInfo.address2,
					address3: billingInfo.address3,
					zipCode: billingInfo.zipCode,
					phone: billingInfo.phone,
				},
			},
		})
		.then(readCart);

const getSubscriptionById = context => (
	subscriptionId,
	subscriptionQuery = getSubscriptionByIDQuery,
) =>
	apolloClient(context)
		.query({
			query: subscriptionQuery,
			variables: { subscriptionID: parseInt(subscriptionId, 10) },
		})
		.then(readSubscriptionData);

const subscriptionWithPaymentMethod = context => (
	subscriptionId,
	subscriptionQuery = SubscriptionWithPaymentMethod,
) =>
	apolloClient(context)
		.query({
			query: subscriptionQuery,
			variables: { subscriptionID: parseInt(subscriptionId, 10) },
		})
		.then(readSubscriptionData);

export const planToUpdate = planCode => planCode && planCode.replace(/_student_/, "_academic_");

const getSubscriptionWithPlanPrices = context => {
	const getSubscription = getSubscriptionById(context);

	return (subscriptionId, purchaseType) =>
		getSubscription(
			subscriptionId,
			purchaseType === "renew" // checking purchaseType
				? getSubscriptionWithRenewPrices
				: getSubscriptionWithAddonPrices,
		);
};

const getOrderById = context => orderNumber => {
	return apolloClient(context)
		.query({
			query: getOrderByIDQuery,
			variables: { orderNumber: parseInt(orderNumber, 10) },
		})
		.then(readOrderData);
};

const getPerpetualById = context => id =>
	apolloClient(context)
		.query({
			query: getPerpetualByIdQuery,
			variables: { id: parseInt(id, 10) },
		})
		.then(readDataFromPerpetual);

export const getSubscriptionsList = context => () =>
	apolloClient(context)
		.query({ query: getSubscriptionsListQuery })
		.then(readSubscriptions);

const getPerpetualsList = context => () =>
	apolloClient(context)
		.query({ query: getPerpetualsQuery })
		.then(readPerpetuals);

export const getRenewableSubscriptions = context => () =>
	apolloClient(context)
		.query({ query: getRenewableSubscriptionsQuery })
		.then(readRenewableSubscriptions);

const getThreePlanPrices = context => () =>
	apolloClient(context)
		.query({
			query: getThreePlanPricesQuery,
		})
		.then(readThreePlanPrices);

export const addActivationToBlackList = context => (serialNumber, uuid) =>
	apolloClient(context)
		.mutate({
			mutation: addActivationToBlackListMutation,
			variables: { serialNumber, uuid },
		})
		.then(() => true);

export const removeActivationFromBlackList = context => (serialNumber, uuid) =>
	apolloClient(context)
		.mutate({
			mutation: removeActivationFromBlackListMutation,
			variables: { serialNumber, uuid },
		})
		.then(() => true);

const eCommerce = nextSingleton(context => ({
	getPrices: getPrices(context),
	getSubscriptionByID: getSubscriptionById(context),
	getSubscriptionWithPaymentMethod: subscriptionWithPaymentMethod(context),
	getSubscriptionWithPlanPrices: getSubscriptionWithPlanPrices(context),
	getOrderByID: getOrderById(context),
	getPerpetualById: getPerpetualById(context),
	getSubscriptionsList: getSubscriptionsList(context),
	getPerpetualsList: getPerpetualsList(context),
	getCart: getCart(context),
	getRenewableSubscriptions: getRenewableSubscriptions(context),
	getThreePlanPrices: getThreePlanPrices(context),
	addActivationToBlackList: addActivationToBlackList(context),
	removeActivationFromBlackList: removeActivationFromBlackList(context),
}));

export const purchase = ({ token, paymentType, cartAsJSON, licenseName }) =>
	apolloClient()
		.mutate({
			mutation: purchaseMutation,
			variables: {
				purchaseInput: {
					token,
					paymentType,
					cartAsJSON,
					licenseName,
				},
			},
		})
		.then(readPaymentResult);

export const updatePaymentMethod = (subscriptionID, token) =>
	apolloClient()
		.mutate({
			mutation: updatePaymentMethodMutation,
			variables: {
				subscriptionID,
				token,
			},
		})
		.then(() => true);

export const updateSubscriptionByID = ({ subscriptionID, planCode }, newSeats) =>
	apolloClient()
		.mutate({
			mutation: updateSubscriptionMutation,
			variables: {
				subscriptionID,
				newSeats,
				planCode,
			},
		})
		.then(readUpdateSubscriptionResult);

export const cancelSubscription = ({ subscriptionID, planCode }) =>
	apolloClient()
		.mutate({
			mutation: cancelSubscriptionMutation,
			variables: {
				subscriptionID,
				planCode,
			},
		})
		.then(() => true);

export const cancelSubscriptionWithSurvey = ({ subscriptionId, cancellationSurvey, planCode }) =>
	apolloClient()
		.mutate({
			mutation: cancelSubscriptionWithSurveyMutation,
			variables: {
				subscriptionId,
				cancellationSurvey,
				planCode,
			},
		})
		.then(readCancelSubscriptionByIDV2Result);

export const reactivateSubscription = ({ subscriptionID, planCode }) =>
	apolloClient()
		.mutate({
			mutation: reactivateSubscriptionMutation,
			variables: {
				subscriptionID,
				planCode,
			},
		})
		.then(readReactivateSubscriptionByIDV2Result);

export const licensesResolvers = {
	subscriptions: context => eCommerce(context).getSubscriptionsList(),
	perpetuals: context => eCommerce(context).getPerpetualsList(),
};

/** @type {(serialNumber: { code: string }) => boolean} */
export const isSnSuccessfull = ({ code } = {}) =>
	code !== SN.updateFailed && code !== SN.creationFailed;

export default eCommerce;
